import { apiRequest } from "@services/apiClient";
import promoCodeBatchesEndpoints from "@services/promoCodeBatches/promoCodeBatchesEndpoints";

const emptyPagination = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 1,
};

function normalizePagination(pagination = {}, fallbackTotal = 0) {
  const total = Number(pagination?.total ?? fallbackTotal ?? 0);
  const limit = Number(pagination?.limit || pagination?.pageSize || 20);
  return {
    page: Number(pagination?.page || 1),
    limit,
    total,
    totalPages: Math.max(
      1,
      Number(pagination?.totalPages || Math.ceil(total / Math.max(1, limit)) || 1),
    ),
  };
}

function normalizeBatchListBody(body = {}) {
  const batches = Array.isArray(body?.batches)
    ? body.batches
    : Array.isArray(body?.items)
      ? body.items
      : Array.isArray(body)
        ? body
        : [];
  return {
    batches,
    pagination: normalizePagination(body?.pagination || emptyPagination, batches.length),
  };
}

function normalizeBatchDetailBody(body = {}) {
  const batch = body?.batch && typeof body.batch === "object" ? body.batch : body;
  if (!batch || typeof batch !== "object" || Array.isArray(batch)) return null;
  const codes = Array.isArray(batch?.codes)
    ? batch.codes
    : Array.isArray(body?.codes)
      ? body.codes
      : [];
  return {
    ...batch,
    codes,
    codesPagination: normalizePagination(
      body?.pagination || batch?.pagination || batch?.codesPagination || {},
      codes.length,
    ),
  };
}

export async function listPromoCodeBatchesRequest(params) {
  const response = await apiRequest(promoCodeBatchesEndpoints.list(params));
  return { ...response, body: normalizeBatchListBody(response?.body) };
}

export async function getPromoCodeBatchDetailRequest(id, params) {
  const response = await apiRequest(promoCodeBatchesEndpoints.byId(id, params));
  return { ...response, body: normalizeBatchDetailBody(response?.body) };
}

export async function createPromoCodeBatchRequest(payload) {
  const response = await apiRequest(promoCodeBatchesEndpoints.base, {
    method: "POST",
    body: payload,
  });
  return { ...response, body: normalizeBatchDetailBody(response?.body) };
}

export async function deletePromoCodeBatchRequest(id) {
  return apiRequest(promoCodeBatchesEndpoints.remove(id), {
    method: "DELETE",
  });
}

export async function exportPromoCodeBatchCsvRequest(id, status = "all") {
  const baseUrl = String(process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  const endpoint = promoCodeBatchesEndpoints.exportCsv(id, status);
  const response = await fetch(`${baseUrl}${endpoint}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "text/csv" },
  });
  if (!response.ok) {
    let message = "Could not export promo code batch.";
    try {
      const payload = await response.json();
      message = payload?.message || message;
    } catch (error) {
      void error;
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") || "";
  const fileNameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  const fileName = fileNameMatch?.[1]
    ? decodeURIComponent(fileNameMatch[1].replace(/"/g, ""))
    : `promo-code-batch-${id}-${status}.csv`;
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(downloadUrl);
}
